import { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import liff from '@line/liff';
import { LiffMockPlugin } from '@line/liff-mock';

import { RootState } from 'store';
import { successLiffLogin, failureLiffLogin } from 'store/liffUser';

// type
type QueryStatus = {
  loading: boolean;
  error: string;
  actLoginLiff: () => void;
  userToken: string | undefined | null;
};

/**
 * LIFFアプリの初期化を行います
 */
export const useAuthLiff = (): QueryStatus => {
  const dispatch = useDispatch();
  const { userToken } = useSelector((state: RootState) => state.liffUser);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const actLoginLiff = useCallback(() => {
    (async () => {
      try {
        setLoading(true); // 処理開始

        // LIFFアプリにログインする
        // アプリバリエーション変数を元に参照するLIFF IDの環境変数を切り替える
        const liffId = process.env.REACT_APP_LIFF_ID as string;
        const nodeEnv = process.env.REACT_APP_NODE_ENV as string;

        if (nodeEnv === 'development') {
          // liffの初期化をモックする
          liff.use(new LiffMockPlugin());
          await liff.init({ liffId, mock: true });
        } else {
          // IDトークンの期限切れ対策：期限切れの場合はローカルストレージのトークン情報を消去し、再ログインを促す
          // liff.init()の前に実行する
          clearExpiredIdToken(liffId);

          await liff.init({ liffId });

          // 開発用にブラウザで実行している場合はログインを促す
          if (!liff.isInClient() && !liff.isLoggedIn()) {
            return liff.login();
          }
        }

        const lineIdToken = liff.getIDToken();
        if (lineIdToken) {
          // サーバー上でLINE ID Tokenを元にLINEユーザーIDを取得する
          // LINEユーザーIDをJWTに格納したuserTokenを受け取り、以降のAPIアクセスではこれを使用
          const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';
          // 状態からユーザートークンを取得します
          const fetchRes = await fetch(`${baseUrl}/user-token`, {
            headers: {
              'line-id-token': lineIdToken,
            },
          });
          const resBody = (await fetchRes.json()) as { lineUserId: string; userToken: string };
          dispatch(
            successLiffLogin({
              userToken: resBody.userToken,
              lineUserId: resBody.lineUserId,
            })
          );
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        const message = err.message ?? err;
        setError(message);
        dispatch(failureLiffLogin());
        alert(`LIFF initialize failed ${message}`);
      } finally {
        setLoading(false); // 処理終了
      }
    })();
  }, [dispatch]);

  // 戻り値
  return { loading, error, actLoginLiff, userToken };

  // // LINE ログインなしで開発するときのダミー
  // const dispatch = useDispatch();
  // const dummyUserId = process.env.REACT_APP_USER_ID || '';
  // dispatch(updateUserId(dummyUserId));
  // return { response: dummyUserId, loading: false, error: '' };
};

//----------------------------------------
// IDトークンの期限切れ対策
// IDトークンの有効期限が1時間で切れるが、ローカルストレージに保存されているトークンの期限が切れている場合でも
// liff.isLoggedIn()がtrueを返す問題があり、その場合にAPIの認証が通らず再ログインもできない状態になる
// 以下の記事を参考に、トークンが期限切れの場合にローカルストレージの内容を消すことでこれを対処している
// https://zenn.dev/arahabica/articles/274bb147a91d8a

// liff関連のlocalStorageのキーのリストを取得
const getLiffLocalStorageKeys = (prefix: string) => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.indexOf(prefix) === 0) {
      keys.push(key);
    }
  }
  return keys;
};
// 期限切れのIDTokenをクリアする
const clearExpiredIdToken = (liffId: string) => {
  const keyPrefix = `LIFF_STORE:${liffId}:`;
  const key = keyPrefix + 'decodedIDToken';
  const decodedIDTokenString = localStorage.getItem(key);
  if (!decodedIDTokenString) {
    return;
  }
  const decodedIDToken = JSON.parse(decodedIDTokenString);

  // 有効期限をチェック
  if (new Date().getTime() > decodedIDToken.exp * 1000) {
    const keys = getLiffLocalStorageKeys(keyPrefix);
    keys.forEach(function (key) {
      localStorage.removeItem(key);
    });
  }

  // デバッグ用：有効期限が必ず切れるようにする（実際のトークン有効期限の1日前に有効期限を設置する）
  // if (new Date().getTime() > decodedIDToken.exp * 1000 - 1000 * 60 * 60 * 24) {
  //   const keys = getLiffLocalStorageKeys(keyPrefix);
  //   keys.forEach(function (key) {
  //     localStorage.removeItem(key);
  //   });
  // }
};
